import { Reveal, Eyebrow } from "./Reveal";

const PROFILE_FIELDS = [
    { label: "Formazione", value: ["Laurea in Psicologia", "Specializzazione in Psicoterapia Cognitivo-Comportamentale — Scuola di Psicoterapia di Basilicata, Potenza", "Master in Ipnosi Clinica Ericksoniana — Scuola IKOS Ageform, Bari"] },
    { label: "Qualifiche professionali", value: "Psicologo · Psicoterapeuta cognitivo-comportamentale · Ipnologo clinico ericksoniano" },
    { label: "Iscrizione all'Albo", value: "Albo degli Psicologi — Sezione A, n. 9539" },
    { label: "Approccio terapeutico", value: "Un approccio integrato che ha nella psicoterapia cognitivo-comportamentale la propria base e integra, in funzione della persona e degli obiettivi terapeutici, ipnosi clinica ericksoniana, psicoeducazione, lavoro sulle emozioni, metafore e altri strumenti psicoterapeutici." },
    { label: "Aree di intervento", value: "Percorsi psicologici e psicoterapeutici individuali · Ansia, stress e attacchi di panico · Difficoltà nella regolazione emotiva · Difficoltà relazionali e di coppia · Difficoltà legate alla sessualità · Percorsi di consapevolezza e crescita personale" },
    { label: "Esperienza professionale", value: "Attività clinica privata presso lo studio di Melfi (PZ), con adolescenti, adulti, coppie e famiglie." },
    { label: "Attività formative e divulgative", value: "Autore di «Prima di pensare. Perché facciamo cose che non volevamo fare» (Armando Editore) · Attività di divulgazione psicologica attraverso incontri pubblici, eventi e contenuti dedicati alla psicologia e alla consapevolezza." },
];

export const Dottore = () => (
    <section id="il-dottore" className="bg-sand/70" data-testid="dottore-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-20 md:py-32">
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-20 items-start">
                <Reveal className="lg:sticky lg:top-28">
                    <div className="relative">
                        <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl shadow-2xl shadow-petrol/20 aspect-[4/5] bg-cream">
                            <img
                                src="/images/foto2.webp"
                                alt="Il Dott. Giovanni De Lorenzo, psicologo e psicoterapeuta, in un momento di ascolto e riflessione"
                                className="h-full w-full object-cover"
                                loading="lazy"
                                data-testid="dottore-portrait"
                            />
                        </div>
                        <div className="absolute -top-6 -right-4 sm:-right-10 w-32 sm:w-44 overflow-hidden rounded-3xl shadow-xl border-4 border-cream rotate-3 aspect-square bg-sand">
                            <img
                                src="/images/foto3.webp"
                                alt="Il Dott. Giovanni De Lorenzo durante il suo percorso di formazione specialistica"
                                className="h-full w-full object-cover"
                                loading="lazy"
                                data-testid="dottore-formazione-photo"
                            />
                        </div>
                        <div className="absolute -bottom-5 right-6 rounded-2xl bg-petrol text-cream px-6 py-4 shadow-xl">
                            <p className="text-xs uppercase tracking-[0.2em] text-cream/70">Psicologo · Psicoterapeuta</p>
                            <p className="font-serif text-xl font-semibold">Dott. Giovanni De Lorenzo</p>
                        </div>
                    </div>
                </Reveal>

                <div>
                    <Reveal>
                        <Eyebrow>Il professionista</Eyebrow>
                        <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-snug text-ink" data-testid="dottore-title">
                            Conosci il Dott. Giovanni De Lorenzo
                        </h2>
                        <p className="mt-4 text-sm sm:text-base font-semibold uppercase tracking-[0.15em] text-terracotta">
                            Psicologo · Psicoterapeuta cognitivo-comportamentale · Ipnologo clinico ericksoniano
                        </p>
                        <div className="mt-7 space-y-4 text-base sm:text-lg text-[#526063] leading-relaxed">
                            <p className="text-ink font-medium">
                                Il mio lavoro nasce dall'incontro tra competenza clinica e curiosità verso la persona.
                            </p>
                            <p>
                                Ogni storia porta con sé un modo particolare di vivere le emozioni, affrontare le difficoltà, costruire relazioni, vivere il proprio corpo e la propria sessualità. Per questo non credo nei percorsi costruiti a partire da un modello rigido.
                            </p>
                            <p>
                                La mia formazione cognitivo-comportamentale rappresenta una base importante, che nel lavoro clinico dialoga con l'ipnosi ericksoniana, la psicoeducazione, il lavoro sulle emozioni, le metafore e la terapia sessuale, quando la sessualità entra nella sofferenza della persona o della coppia.
                            </p>
                            <p>
                                Non significa usare tutto. Significa scegliere ciò che può essere utile a quella persona, in quel momento, per comprendere meglio ciò che sta vivendo e poterlo affrontare in modo diverso.
                            </p>
                            <p>
                                La terapia, per me, non è un insieme di tecniche da applicare. È un lavoro condiviso, nel quale terapeuta e paziente cercano di dare senso a ciò che accade e trasformare la consapevolezza in possibilità concreta di cambiamento.
                            </p>
                        </div>
                        <blockquote className="mt-8 border-l-2 border-terracotta pl-6 font-serif italic text-xl sm:text-2xl text-petrol leading-relaxed">
                            "Perché comprendere una persona richiede più di una teoria. Richiede la capacità di guardarla nella sua interezza."
                        </blockquote>
                    </Reveal>

                    <Reveal delay={0.15} className="mt-12">
                        <div className="rounded-3xl bg-white p-8 sm:p-10 shadow-sm border border-ink/5">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-ink">Profilo professionale</h3>
                            </div>
                            <dl className="mt-8 divide-y divide-ink/5">
                                {PROFILE_FIELDS.map((f, i) => (
                                    <div key={f.label} className="py-4 grid sm:grid-cols-[200px_1fr] gap-1 sm:gap-6" data-testid={`profile-field-${i}`}>
                                        <dt className="text-sm font-semibold text-ink">{f.label}</dt>
                                        <dd className="text-sm text-[#526063] leading-relaxed">
                                            {Array.isArray(f.value)
                                                ? f.value.map((line, idx) => <span key={idx} className="block">{line}</span>)
                                                : f.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    </section>
);
